import { useEffect, useRef, useState } from "react"
import { DEMOS } from "@/components/DeckBrowser/patternDemos"
import { DECK_DETAIL_COPY } from "@/content/cardsRound"
import styles from "@/styles/components/PatternDemo.module.css"

/*
 * Plays one pattern demo from `patternDemos`. The demos are imperative DOM (a
 * faithful port of the handoff), so this drives them the handoff's way rather
 * than through JSX: it drops the markup into a fresh box, wires `init`, and starts
 * `run` when the stage scrolls into view.
 *
 * The box is RECREATED on every mount and replay. A ported `run` loop bails the
 * moment its root is `!isConnected`, so removing the old box is what cancels a
 * half-finished animation — on replay, on unmount, and on React's dev double
 * mount. The demo markup is `aria-hidden`: it is an illustration, and the
 * pattern's name and "use it when" line beside it carry the meaning.
 */

// Restart / replay — a counter-clockwise arrow, no label.
const REPLAY_ICON = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
    <path d="M3 3v5h5" />
  </svg>
)

interface PatternDemoProps {
  id: string
}

export default function PatternDemo({ id }: PatternDemoProps) {
  const demo = DEMOS[id]
  const stageRef = useRef<HTMLDivElement>(null)
  const [replayNonce, setReplayNonce] = useState(0)

  useEffect(() => {
    const stage = stageRef.current
    if (!stage || !demo) return

    const box = document.createElement("div")
    box.className = styles.box
    box.setAttribute("aria-hidden", "true")
    box.innerHTML = demo.html
    stage.appendChild(box)
    demo.init?.(box)

    let observer: IntersectionObserver | null = null
    if (demo.run) {
      // Start when the stage is actually on screen, so a sheet full of demos does
      // not all animate at once. On replay the stage is already visible, so this
      // fires straight away.
      observer = new IntersectionObserver(
        (entries, obs) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              obs.disconnect()
              demo.run?.(box)
            }
          }
        },
        { threshold: 0.5 }
      )
      observer.observe(box)
    }

    return () => {
      observer?.disconnect()
      box.remove()
    }
  }, [demo, replayNonce])

  if (!demo) return null

  return (
    <div className={styles.stage} ref={stageRef}>
      {/* Controls row: the "Try it" hint (for interactive demos) sits top-left,
          the Replay button top-right. "Try it" is a label, not a button — the
          demo itself is the interactive thing. */}
      <div className={styles.controls}>
        {demo.try ? <span className={styles.try}>{DECK_DETAIL_COPY.tryIt}</span> : null}
        <button
          type="button"
          className={styles.replay}
          aria-label={DECK_DETAIL_COPY.replay}
          title={DECK_DETAIL_COPY.replay}
          onClick={() => setReplayNonce((n) => n + 1)}
        >
          {REPLAY_ICON}
        </button>
      </div>
      {/* The demo box is appended here imperatively in the effect, below the
          controls row (or filling the whole stage when there are no controls). */}
    </div>
  )
}
